import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { formatCalendarDate, todayCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency, fmtHours } from '../../lib/display-labels';
import { reportHoursText } from '../../lib/shift-hours';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { workerIdOf } from '../../lib/worker-id';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleLabel } from '../../lib/roles';
import { useVerticalLabel } from '../../hooks/useVerticalCatalog';
import { ticketHoursTotalText } from '../../lib/pay-entry-hours';
import { LOAD_FAILURE_QUERY_OPTIONS, queryLoadFailure } from '../../lib/load-failure';
import {
  ChevronDown,
  Clock,
  Users,
  DollarSign,
  Timer,
  Loader2,
  Eye,
  AlertCircle,
  Ticket,
} from 'lucide-react';

/* ── Colors ── */
const C = {
  bg: 'hsl(var(--background))',
  card: 'hsl(var(--background))',
  border: 'hsl(var(--border))',
  amber: '#ffc965',
  green: '#96f7af',
  red: '#ff7351',
  text: '#e6edf3',
  muted: '#8b949e',
} as const;

/* ── Types ── */
interface PayPeriod {
  id: string;
  period_number?: number;
  period_start: string;
  period_end: string;
  status: string;
}

interface SummaryWorker {
  /**
   * `worker_id` is what this screen used to read, and the endpoint has never
   * returned it. BOTH branches of GET /payroll/reports/pay-summary — tickets
   * and timesheets — map their rows to `user_id`. `worker_name` IS returned,
   * so every row rendered correctly and only the drill-in link was built from
   * undefined. Read through workerIdOf, which tolerates either spelling.
   */
  user_id?: string;
  worker_id?: string;
  worker_name: string;
  role_name?: string;
  primary_vertical?: string;
  pay_basis?: string;
  /**
   * Timesheets branch, since api#825 — the pay-category split per worker.
   * `doubletime_hours` is sent too and has no column here.
   */
  regular_hours?: number | null;
  overtime_hours?: number | null;
  /**
   * Timesheets branch only (api#824). Hours of record — the rounded shift
   * where rounding is on — and the raw punches beside it, which the API sends
   * as null unless they differ. Render through reportHoursText.
   */
  hours_of_record?: number | string | null;
  clocked_hours?: number | string | null;
  ticket_count?: number;
  /**
   * Tickets branch only (api#836). The worker's ticket hours with each TICKET
   * counted once — a hand on two lines of one ticket holds two pay rows and
   * one span. Display only; null when no ticket's hours are known. Rendered
   * as the API sent it: summing rows here would count a ticket per line.
   */
  ticket_hours?: number | null;
  ticket_hours_unresolved_tickets?: number;
  gross_pay: number;
  status?: string;
}

/* ── Helpers ── */
/**
 * period_start/period_end are calendar days. The `+ 'T00:00:00'` splice was the
 * right idea and the right output; it just had no guard, so an absent period
 * bound rendered the string "Invalid Date". @see formatCalendarDate.
 */
const formatDate = (iso: string | null | undefined): string =>
  formatCalendarDate(iso, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—';

const formatDateShort = (iso: string | null | undefined): string =>
  formatCalendarDate(iso, { month: 'short', day: 'numeric' }) ?? '—';

/** See TimesheetReports — same contract, same one-decimal house style. */
const formatHours = (hrs: number | string | null | undefined): string => fmtHours(hrs, 1);

function statusPill(status: string) {
  const s = status.toLowerCase();
  if (s === 'exported') return { label: 'Exported', bg: 'rgba(150,247,175,0.15)', color: C.green };
  if (s === 'locked' || s === 'approved') return { label: s === 'approved' ? 'Locked' : 'Locked', bg: 'rgba(255,201,101,0.15)', color: C.amber };
  return { label: 'Open', bg: 'rgba(139,148,158,0.15)', color: C.muted };
}

function workerStatusPill(statuses: string[]): { label: string; bg: string; color: string } {
  if (statuses.some((s) => s === 'rejected')) return { label: 'Has rejected', bg: 'rgba(255,115,81,0.15)', color: C.red };
  if (statuses.every((s) => s === 'approved')) return { label: 'All approved', bg: 'rgba(150,247,175,0.15)', color: C.green };
  return { label: 'Pending', bg: 'rgba(255,201,101,0.15)', color: C.amber };
}

/**
 * The refusal test, and the failure test beside it.
 *
 * Both come from `lib/load-failure` now rather than being declared here, so
 * this screen and the shared `LoadFailureNotice` cannot drift about what
 * counts as a refusal. The RENDERING below stays local: this screen is drawn
 * from the `C.*` style-object palette rather than Tailwind, and a Tailwind
 * dropped into it would be the one element on the page that does not follow
 * notice dropped into it would be the one element on the page that does not
 * follow the theme.
 */

/**
 * Rendered INSTEAD OF the whole report — tiles included — for a caller the
 * server will refuse.
 *
 * THREE STATES, NEVER TWO. Until the API gate landed this screen had no error
 * branch at all: `useQuery` was destructured `{ data, isLoading }`, so a 403 left
 * `summaryData` undefined and every `?? 0` downstream turned a refusal into
 * **Total Workers 0** and **Total Gross Pay $0.00**, over the empty state "No pay
 * data for this period". That is a factual claim about payroll, made to someone
 * who was told nothing of the kind.
 *
 * WHY IT REPLACES THE TILES AND NOT JUST THE TABLE. Swapping only the table
 * would leave `$0.00` sitting above the notice — the same false statement, in
 * larger type. A refusal has no totals.
 *
 * WHY THE NOTICE IS NOT ROUTED THROUGH ANYTHING THAT ALREADY EXISTS HERE (#407).
 * #421 fixed this exact family on the pay-profile screens and moved the bug one
 * layer over: it sent the refusal into the screen's existing toast, which is
 * green with a checkmark, so "You don't have permission" rendered under a tick
 * from #421 until #452. A component that has only ever displayed successes has
 * no opinion about refusals, and inheriting its styling is not a decision
 * anybody made. This is its own element, with the neutral card treatment
 * `NoShopPayAccessNotice` uses on the sibling screen.
 */
function PaySummaryUnreachableNotice() {
  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '40px 24px',
        textAlign: 'center',
      }}
    >
      <AlertCircle size={40} style={{ color: C.muted, margin: '0 auto 12px' }} />
      <div style={{ color: C.text, fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
        Couldn&rsquo;t load the payroll summary
      </div>
      <div style={{ color: C.muted, fontSize: 14, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
        This is a problem reaching the server, not a statement about whether anyone was paid this
        period. Try again in a moment.
      </div>
    </div>
  );
}

function NoPaySummaryAccessNotice({ role }: { role?: string }) {
  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '40px 24px',
        textAlign: 'center',
      }}
    >
      <AlertCircle size={40} style={{ color: C.muted, margin: '0 auto 12px' }} />
      <div style={{ color: C.text, fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
        You don&rsquo;t have permission to view the payroll summary
      </div>
      <div style={{ color: C.muted, fontSize: 14, maxWidth: 560, margin: '0 auto', lineHeight: 1.6 }}>
        What each worker was paid is limited to admins and office staff
        {role ? ` — yours is ${getRoleLabel(role)}` : ''}. This is not a statement about
        whether anyone was paid this period; it is a statement about what you can see.
      </div>
    </div>
  );
}

/* ── Component ── */
export default function PayrollSummaryScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const timeZone = useTenantTimeZone();
  const verticalLabel = useVerticalLabel();
  const [selectedPeriodId, setSelectedPeriodId] = useState<string | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Fetch pay periods
  const { data: periodsData, isLoading: periodsLoading } = useQuery({
    queryKey: ['pay-periods-list'],
    queryFn: () => api.payPeriods.list({ limit: 50, offset: 0 }).then((r) => r.data?.data ?? r.data ?? []),
  });

  const periods: PayPeriod[] = useMemo(() => {
    const raw = Array.isArray(periodsData) ? periodsData : [];
    return [...raw].sort((a, b) => b.period_start.localeCompare(a.period_start));
  }, [periodsData]);

  // Auto-select: prefer period containing today, then most recent with timesheets, then first
  const defaultPeriodId = useMemo(() => {
    if (periods.length === 0) return null;
    // The TENANT's today (companies.time_zone). No zone, no match.
    const today = todayCalendarDate(timeZone);
    const current = today
      ? periods.find((p) => p.period_start <= today && today <= p.period_end)
      : undefined;
    if (current) return current.id;
    const withTimesheets = periods.find((p) => p.status !== 'empty');
    if (withTimesheets) return withTimesheets.id;
    return periods[0].id;
  }, [periods, timeZone]);

  const activePeriodId = selectedPeriodId ?? defaultPeriodId;
  const activePeriod = periods.find((p) => p.id === activePeriodId);

  // Fetch pay summary for selected period
  const summaryQuery = useQuery({
    queryKey: ['pay-summary', activePeriodId],
    enabled: !!activePeriodId,
    queryFn: () => api.payrollReports.paySummary(activePeriodId!).then((r) => r.data?.data ?? r.data),
    // A 403 is an answer, not a flake. Retrying it delays the notice and tells
    // the user nothing new. Matches TimesheetReports' shop query.
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const { data: summaryData, isLoading: summaryLoading } = summaryQuery;
  // `queryLoadFailure`, not `summaryError`: a query waiting to retry reports
  // `error: null` and keeps the failure on `failureReason`, so reading `error`
  // alone leaves a 500 rendering "No pay data for this period".
  const summaryFailure = queryLoadFailure(summaryQuery);
  const summaryForbidden = summaryFailure === 'refused';

  /**
   * THE OTHER HALF OF THE SAME BUG, LEFT BEHIND WHEN THE 403 WAS FIXED.
   *
   * `summaryForbidden` caught the refusal and nothing caught anything else. A
   * 500, a timeout or a dead connection still fell through to `workers = []`
   * and rendered "No pay data for this period" over tiles reading **Total
   * Workers 0** and **$0.00** — the identical false statement the refusal
   * notice above was written to remove, arriving by the other door.
   */
  const summaryUnreachable = summaryFailure === 'unreachable';

  const workers: SummaryWorker[] = summaryData?.workers ?? [];
  const paySource: string = summaryData?.pay_source ?? 'timesheets';
  const totalWorkers = summaryData?.totals?.worker_count ?? 0;
  const totalGross = summaryData?.totals?.gross_pay ?? 0;
  // Every card below reads `totals` as the API sent it (api#825); nothing is
  // added up here. ticket_count is DISTINCT tickets in the period — a ticket
  // worked by three hands is one ticket — which is exactly what the old sum of
  // per-worker ticket_count got wrong. A key the API did not send reads —.
  const totals: any = summaryData?.totals ?? null;

  const periodLabel = (p: PayPeriod) =>
    `Period #${p.period_number ?? '—'} — ${formatDateShort(p.period_start)}–${formatDate(p.period_end)}`;

  const loading = periodsLoading || summaryLoading;

  return (
    <div style={{ background: C.bg, minHeight: '100vh' }}>
      

      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '24px 16px' }}>
        {/* ── Header row ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ color: C.text, fontSize: 24, fontWeight: 700, margin: 0 }}>Payroll Summary</h1>

            {/* Period selector */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setDropdownOpen((o) => !o)}
                style={{
                  background: C.card,
                  border: `1px solid ${C.border}`,
                  borderRadius: 8,
                  padding: '8px 14px',
                  color: C.text,
                  fontSize: 14,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  minWidth: 260,
                }}
              >
                {activePeriod ? periodLabel(activePeriod) : 'Select period…'}
                <ChevronDown size={16} style={{ color: C.muted }} />
              </button>
              {dropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: 4,
                    background: C.card,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    minWidth: 300,
                    maxHeight: 300,
                    overflowY: 'auto',
                    zIndex: 50,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  }}
                >
                  {periods.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedPeriodId(p.id); setDropdownOpen(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        padding: '10px 14px',
                        background: p.id === activePeriodId ? 'rgba(255,201,101,0.1)' : 'transparent',
                        border: 'none',
                        color: C.text,
                        fontSize: 14,
                        cursor: 'pointer',
                      }}
                    >
                      {periodLabel(p)}
                    </button>
                  ))}
                  {periods.length === 0 && (
                    <div style={{ padding: '12px 14px', color: C.muted, fontSize: 14 }}>No pay periods found</div>
                  )}
                </div>
              )}
            </div>

            {/* Status pill */}
            {activePeriod && (() => {
              const sp = statusPill(activePeriod.status);
              return (
                <span style={{ background: sp.bg, color: sp.color, padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 600 }}>
                  {sp.label}
                </span>
              );
            })()}
          </div>

        </div>

        {/* ── Loading state ── */}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
            <Loader2 size={28} style={{ color: C.amber }} className="animate-spin" />
          </div>
        )}

        {/* ── Content ── */}
        {/* A refusal replaces the ENTIRE report, tiles included — see
            NoPaySummaryAccessNotice. Rendering totals beside it would restate
            the $0.00 this notice exists to remove. */}
        {!loading && activePeriod && summaryForbidden && (
          <NoPaySummaryAccessNotice role={user?.role} />
        )}

        {/* Replaces the whole report for the same reason a refusal does: the
            tiles would otherwise restate the $0.00 as a measurement. */}
        {!loading && activePeriod && summaryUnreachable && <PaySummaryUnreachableNotice />}

        {!loading && activePeriod && !summaryForbidden && !summaryUnreachable && (
          <>
            {/* Summary stats bar */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
              {[
                { icon: <Users size={20} />, label: 'Total Workers', value: totalWorkers.toString(), sub: undefined as string | undefined },
                ...(paySource === 'tickets'
                  ? [{ icon: <Ticket size={20} />, label: 'Total Tickets', value: String(displayValue(totals?.ticket_count)) }]
                  : [
                      // Clocked goes on a sub-line, not in the value: at card
                      // size "78.50h (clocked 78.62h)" wraps. Present exactly
                      // when the API sent clocked_hours (null unless different).
                      { icon: <Timer size={20} />, label: 'Total Hours', value: reportHoursText(totals?.hours_of_record, null),
                        sub: totals?.clocked_hours == null ? undefined : `clocked ${reportHoursText(totals.clocked_hours, null)}` },
                      { icon: <Clock size={20} />, label: 'Total OT Hours', value: formatHours(totals?.overtime_hours) },
                    ]),
                { icon: <DollarSign size={20} />, label: 'Total Gross Pay', value: fmtCurrency(totalGross) },
              ].map((card) => (
                <div
                  key={card.label}
                  style={{
                    background: C.card,
                    border: `1px solid ${C.border}`,
                    borderRadius: 12,
                    padding: '20px 16px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: C.muted, fontSize: 13, marginBottom: 8 }}>
                    {card.icon}
                    {card.label}
                  </div>
                  <div style={{ color: C.text, fontSize: 28, fontWeight: 700 }}>{card.value}</div>
                  {card.sub && <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>{card.sub}</div>}
                </div>
              ))}
            </div>

            {/* Worker table */}
            {workers.length === 0 ? (
              <div style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                padding: '60px 20px',
                textAlign: 'center',
              }}>
                <Clock size={40} style={{ color: C.muted, margin: '0 auto 12px' }} />
                <div style={{ color: C.muted, fontSize: 16 }}>No pay data for this period</div>
              </div>
            ) : (
              <div style={{
                background: C.card,
                border: `1px solid ${C.border}`,
                borderRadius: 12,
                overflow: 'hidden',
              }}>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                        {(paySource === 'tickets'
                          ? ['Worker Name', 'Role', 'Vertical', 'Tickets', 'Ticket Hours', 'Gross Pay', 'Actions']
                          : ['Worker Name', 'Role', 'Pay Basis', 'Hours of Record', 'Regular Hrs', 'OT Hrs', 'Gross Pay', 'Status', 'Actions']
                        ).map((h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: 'left',
                              padding: '14px 16px',
                              color: C.muted,
                              fontSize: 12,
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {workers.map((w) => (
                        <tr key={workerIdOf(w) ?? w.worker_name} style={{ borderBottom: `1px solid ${C.border}` }}>
                          <td style={{ padding: '14px 16px', color: C.text, fontSize: 14, fontWeight: 500, whiteSpace: 'nowrap' }}>{w.worker_name}</td>
                          <td style={{ padding: '14px 16px', color: C.muted, fontSize: 14 }}>{w.role_name ?? '—'}</td>
                          {paySource === 'tickets' ? (
                            <>
                              <td style={{ padding: '14px 16px', color: C.muted, fontSize: 14 }}>{w.primary_vertical ? verticalLabel(w.primary_vertical) : '—'}</td>
                              <td style={{ padding: '14px 16px', color: C.text, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{w.ticket_count ?? 0}</td>
                              <td style={{ padding: '14px 16px', color: C.text, fontSize: 14, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{ticketHoursTotalText(w.ticket_hours, w.ticket_hours_unresolved_tickets)}</td>
                            </>
                          ) : (
                            <>
                              <td style={{ padding: '14px 16px', color: C.muted, fontSize: 14 }}>{w.pay_basis ?? '—'}</td>
                              <td style={{ padding: '14px 16px', color: C.text, fontSize: 14, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{reportHoursText(w.hours_of_record, w.clocked_hours)}</td>
                              <td style={{ padding: '14px 16px', color: C.text, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{formatHours(w.regular_hours)}</td>
                              <td style={{ padding: '14px 16px', color: (Number(w.overtime_hours) || 0) > 0 ? C.amber : C.text, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>{formatHours(w.overtime_hours)}</td>
                            </>
                          )}
                          <td style={{ padding: '14px 16px', color: C.green, fontSize: 14, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{fmtCurrency(w.gross_pay)}</td>
                          {paySource === 'timesheets' && (
                            <td style={{ padding: '14px 16px' }}>
                              {(() => {
                                const ws = workerStatusPill([w.status ?? 'pending']);
                                return (
                                  <span style={{ background: ws.bg, color: ws.color, padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                    {ws.label}
                                  </span>
                                );
                              })()}
                            </td>
                          )}
                          <td style={{ padding: '14px 16px' }}>
                            <button
                              onClick={() => { const id = workerIdOf(w); if (id) navigate(`/payroll/workers/${id}/pay-profile`); }}
                              disabled={!workerIdOf(w)}
                              style={{
                                background: 'rgba(255,201,101,0.1)',
                                border: `1px solid ${C.amber}`,
                                borderRadius: 6,
                                padding: '6px 14px',
                                color: C.amber,
                                fontSize: 13,
                                fontWeight: 600,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                              }}
                            >
                              <Eye size={14} />
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* No periods at all */}
        {!loading && periods.length === 0 && (
          <div style={{
            background: C.card,
            border: `1px solid ${C.border}`,
            borderRadius: 12,
            padding: '60px 20px',
            textAlign: 'center',
          }}>
            <AlertCircle size={40} style={{ color: C.muted, margin: '0 auto 12px' }} />
            <div style={{ color: C.muted, fontSize: 16 }}>No pay periods found</div>
          </div>
        )}
      </div>
    </div>
  );
}
